import { cardExpiry, CardLabelFields, cardTitle } from '../../../utils/cardLabels';

export interface SavedCardRow extends CardLabelFields {
  id: string;
  isDefault: boolean;
  inUseByBookingIds: readonly string[];
  billingLine1?: string | null;
  billingLine2?: string | null;
  billingCity?: string | null;
  billingState?: string | null;
  billingPostalCode?: string | null;
}

/** Two lines for the profile card: street, then city, state and postal code. */
export function billingAddressLines(card: SavedCardRow): string[] {
  const street = [card.billingLine1, card.billingLine2].filter(Boolean).join(', ');
  const city = [card.billingCity, card.billingState].filter(Boolean).join(', ');
  const cityLine = [city, card.billingPostalCode].filter(Boolean).join(' ');
  return [street, cityLine].filter(Boolean);
}

/** Default card first, then usable cards, then expired ones; Stripe's order otherwise. */
export function sortSavedCards<T extends SavedCardRow>(cards: readonly T[]): T[] {
  const rank = (card: T) => (card.isDefault ? 0 : card.isExpired ? 2 : 1);
  return cards
    .map((card, index) => ({ card, index }))
    .sort((a, b) => rank(a.card) - rank(b.card) || a.index - b.index)
    .map(({ card }) => card);
}

/** What the guest is told after "Remove" (FR-018). */
export function removalMessage(result: {
  removed: boolean;
  hidden: boolean;
  inUseByBookingIds: readonly string[];
}): string {
  if (result.hidden) {
    const trips = result.inUseByBookingIds.length;
    return trips === 1
      ? 'Card removed from your saved cards. It stays on file for one trip until that trip is settled.'
      : `Card removed from your saved cards. It stays on file for ${trips} trips until they are settled.`;
  }
  return 'Card removed.';
}

/** The text of the "Remove card" confirmation. */
export function removalPrompt(card: SavedCardRow): string {
  if (card.inUseByBookingIds.length > 0) {
    return 'This card is used for a trip that is not settled yet. It will no longer be shown at checkout, but that trip can still charge it under your rental agreement.';
  }
  return 'The card will be removed from your account on this site.';
}

export function savedCardMessage(duplicate: boolean): string {
  return duplicate ? 'This card was already saved.' : 'Card saved.';
}

/**
 * The profile page's payment row: the default card (else the first usable one) and how
 * many other cards are saved, or an invitation to add one.
 */
export function paymentMethodsSummary(cards: readonly SavedCardRow[]): {
  title: string;
  detail: string;
  hasCards: boolean;
} {
  if (cards.length === 0) {
    return {
      title: 'No saved cards',
      detail: 'Add a card to pay faster at checkout.',
      hasCards: false,
    };
  }
  const [main] = sortSavedCards(cards);
  const others = cards.length - 1;
  const parts = [main.isDefault ? 'Default' : null, cardExpiry(main)].filter(Boolean);
  if (others > 0) parts.push(`+${others} more`);
  return { title: cardTitle(main), detail: parts.join(' · '), hasCards: true };
}
